import dayjs from 'dayjs';
import { NextRouter } from 'next/router';

/**
 * 공통 유틸리티성 함수(컨트롤에 의존적이지 않은 공통 함수)
 */

/**
 * 전화번호, 핸드폰형식으로 변환
 * @param val 01099887772
 * @param type 0: **** 으로 표시, 1: 모두 표시
 */
export const convertPhoneText = (
  val: string | number | null | undefined,
  type: 0 | 1,
  defaultValue: string = '',
) => {
  if (!val) return defaultValue;

  const tel = val.toString();

  const getReplaceValue = (middleLength: number) =>
    type === 0 ? `$1-${'*'.repeat(middleLength)}-$3` : '$1-$2-$3';

  if (tel.length === 11)
    return tel.replace(/(\d{3})(\d{4})(\d{4})/, getReplaceValue(4));

  if (tel.length === 7) return tel.replace(/(\d{3})(\d{4})/, '$1-$2');

  if (tel.length === 8) return tel.replace(/(\d{4})(\d{4})/, '$1-$2');

  if (tel.indexOf('02') === 0 && tel.length === 10)
    return tel.replace(/(\d{2})(\d{4})(\d{4})/, getReplaceValue(4));

  if (tel.indexOf('02') === 0 && tel.length === 9)
    return tel.replace(/(\d{2})(\d{3})(\d{4})/, getReplaceValue(3));

  return tel.replace(/(\d{3})(\d{3})(\d{4})/, getReplaceValue(3));
};

/**
 *  날짜값을 fotmat(YYYY-MM-DD 형태) 스트링으로 변경해서 반환
 * @param val 변경할 값
 * @param format  날짜 표시 FORMAT (ex, YYYY-MM-DD)
 */
export const convertDateFormat = (
  val: string | Date | null | undefined,
  format: string = 'YYYY-MM-DD',
  defaultValue: string = '',
) => {
  if (!val) return defaultValue;

  return dayjs(val).format(format);
};

/**
 *  돈표시 천단위 콤마 찍어서 반환
 * @param val 대상값
 */
export const convertMoneyText = (
  val: string | number,
  defaultValue: string = '',
) => {
  if (!val) return defaultValue;

  const moneyVal = typeof val === 'number' ? val.toString() : val;

  return moneyVal.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 *  N일 이전 date 구하기
 * @param n n일 이전
 */
export const getDaysBack = (n: number) => {
  const daysBack = new Date();
  daysBack?.setDate(daysBack?.getDate() - n);

  return daysBack;
};

/**
 * 쿼리 파라메터 값 가져오기
 * @param router next router
 */
export const getQueryParm =
  (router: NextRouter) =>
  (field: string, defaultValue: string | number = '') =>
    router.query[field]?.toString().toUpperCase() || defaultValue;

/**
 * Object value가 true인 key만
 */
export const getSelectedBrandList = (obj: Object, list, targetKey: string) => {
  // true인 값만 추출
  const checkedBrandsList = Object.keys(obj).filter((key) => obj[key] === true);

  let result;

  if (checkedBrandsList.includes('All')) {
    // 전체 선택인 경우 모든 브랜드 index 넣어야함
    result = list.map((item) => item[targetKey]);
  } else {
    result = list
      .filter((item) => checkedBrandsList.includes(item.brand_name))
      .map((el) => el[targetKey]);
  }
  return result;
};

/** 기본 default val 반환 */
export const getVal = (
  val: string | number | null | undefined,
  defaultValue: string | number = '',
) => {
  if (!val) return defaultValue;

  return val;
};

/** 클라이언트측 호출인지 여부(true) 반환 */
export const isClient = () => typeof window !== 'undefined';

/** base64 인코딩 */
export const setBase64 = (strValue: string) =>
  Buffer.from(strValue, 'utf8').toString('base64');

/** base64 디코딩 */
export const getBase64 = (encValue: string) =>
  Buffer.from(encValue, 'base64').toString('utf8');

/** 스크롤 상단 이동
 * eleSelect: query selector 에서 쓰일 css classname 또는 엘리먼트 id
 * ex) '.drawer-two-btn'
 */
export const setScrollTop = (
  eleSelect: string,
  scrollBehavior: 'auto' | 'smooth' = 'smooth',
  top: number = 0,
) => {
  const ele = document.querySelector(eleSelect);

  ele?.scroll({ top, left: 0, behavior: scrollBehavior });
};

/** emotion styled props warning 방지를 위한 옵션
 * styled 사용시 props name 에 대문자가 있을 경우 warning이 발생
 * warning msg: in the DOM as a custom attribute, spell it as lowercase `ismobile` instead.
 * */
export const transientProps = {
  shouldForwardProp: (propName: string) => !propName.startsWith('$'),
};

/** os정보, 모바일 여부 true/false 반환 */
export const checkOsMobile = (): {
  os: 'android' | 'ios' | 'other';
  isMobile: boolean;
} => {
  const agent = navigator.userAgent.toLowerCase();

  // 모바일 여부
  const isMobile = /iphone|ipad|ipod|android/i.test(agent);

  // os 안드로이드, ios, other
  const os =
    agent.indexOf('android') > -1
      ? 'android'
      : agent.indexOf('iphone') > -1 ||
        agent.indexOf('ipad') > -1 ||
        agent.indexOf('ipod') > -1
      ? 'ios'
      : agent.indexOf('linux')
      ? 'android'
      : 'other';

  return {
    os,
    isMobile,
  };
};

export const getWindowDimension = () => {
  const hasWindow = typeof window !== 'undefined';
  let height = 0;
  let width = 0;

  if (hasWindow) {
    height = window.innerHeight;
    width = window.innerWidth;
  }

  return { height, width };
};

export const getBrowserName = () => {
  const hasWindow = typeof window !== 'undefined';
  if (!hasWindow) return '';

  let userAgent = navigator.userAgent;
  let browserName;

  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = 'chrome';
  } else if (userAgent.match(/firefox|fxios/i)) {
    browserName = 'firefox';
  } else if (userAgent.match(/safari/i)) {
    browserName = 'safari';
  } else if (userAgent.match(/opr\//i)) {
    browserName = 'opera';
  } else if (userAgent.match(/edg/i)) {
    browserName = 'edge';
  } else {
    browserName = 'No browser detection';
  }

  return browserName;
};
